/**
 * Shell / secondary panel open motion.
 *
 * Rule: never fake-animate react-resizable-panels width (0% -> N% double resize).
 * Mount the column at its target size; animate *content* enter only.
 *
 * Gallery SoT: /?test=maater-ui#panel-motion
 * Preference key: StorageKeys.UI_SHELL_PANEL_MOTION_STYLE
 */

export const SHELL_PANEL_MOTION_MS = 200

/**
 * Auto-hide flyout: how long the pointer may be away before it starts to close.
 * Re-entry inside the hold cancels it. The exit motion runs after the hold.
 */
export const SHELL_FLYOUT_LEAVE_HOLD_MS = 500

/**
 * How long the pointer must stay on the collapsed rail before the flyout opens.
 * A pass across the rail (second monitor) is shorter than this, so it does not flash.
 */
export const SHELL_FLYOUT_ENTER_HOLD_MS = 200

/** Shared easing with existing fade / slide helpers in index.css. */
export const SHELL_PANEL_EASE = 'cubic-bezier(0.4, 0, 0.2, 1)'

export type ShellPanelMotionStyleId = 'nudge' | 'fade' | 'crisp' | 'instant'

export type ShellPanelEnterSide = 'left' | 'right'

export type ShellPanelMotionStyle = {
  id: ShellPanelMotionStyleId
  label: string
  summary: string
}

/** Choosable enter styles - pick in gallery; persisted preference drives SecondaryPanel. */
export const SHELL_PANEL_MOTION_STYLES: readonly ShellPanelMotionStyle[] = [
  {
    id: 'nudge',
    label: 'Soft nudge',
    summary: 'Fade + 8px from the dock edge (matches menu/tooltip language)',
  },
  {
    id: 'fade',
    label: 'Quiet fade',
    summary: 'Opacity only - least motion, good for dense docks',
  },
  {
    id: 'crisp',
    label: 'Crisp slide',
    summary: 'Fade + 16px, slightly snappier duration',
  },
  {
    id: 'instant',
    label: 'Instant',
    summary: 'No content motion - column appears at target width',
  },
] as const

/** Default until Rowan locks a style in the gallery. */
export const SHELL_PANEL_MOTION_DEFAULT: ShellPanelMotionStyleId = 'nudge'

export function isShellPanelMotionStyleId(value: unknown): value is ShellPanelMotionStyleId {
  return (
    value === 'nudge' ||
    value === 'fade' ||
    value === 'crisp' ||
    value === 'instant'
  )
}

export function normalizeShellPanelMotionStyle(
  value: unknown,
): ShellPanelMotionStyleId {
  return isShellPanelMotionStyleId(value) ? value : SHELL_PANEL_MOTION_DEFAULT
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * Tailwind / tw-animate classes for secondary dock content on mount.
 * Empty string when instant or reduced-motion (fade-only fallback for reduced).
 */
export function shellPanelEnterClass(
  style: ShellPanelMotionStyleId = SHELL_PANEL_MOTION_DEFAULT,
  side: ShellPanelEnterSide = 'right',
): string {
  const reduced = prefersReducedMotion()
  const effective: ShellPanelMotionStyleId =
    reduced && style !== 'instant' ? 'fade' : style

  if (effective === 'instant') return ''

  const from =
    side === 'left' ? 'slide-in-from-left-2' : 'slide-in-from-right-2'
  const fromCrisp =
    side === 'left' ? 'slide-in-from-left-4' : 'slide-in-from-right-4'

  switch (effective) {
    case 'fade':
      return 'animate-in fade-in-0 duration-200 fill-mode-both'
    case 'crisp':
      return `animate-in fade-in-0 ${fromCrisp} duration-150 fill-mode-both`
    case 'nudge':
    default:
      return `animate-in fade-in-0 ${from} duration-200 fill-mode-both`
  }
}

/**
 * Reverse of `shellPanelEnterClass`. Same style and duration, toward the dock edge.
 * Empty when instant or when there is nothing to play.
 */
export function shellPanelExitClass(
  style: ShellPanelMotionStyleId = SHELL_PANEL_MOTION_DEFAULT,
  side: ShellPanelEnterSide = 'right',
): string {
  const reduced = prefersReducedMotion()
  const effective: ShellPanelMotionStyleId =
    reduced && style !== 'instant' ? 'fade' : style

  if (effective === 'instant') return ''

  const to =
    side === 'left' ? 'slide-out-to-left-2' : 'slide-out-to-right-2'
  const toCrisp =
    side === 'left' ? 'slide-out-to-left-4' : 'slide-out-to-right-4'

  switch (effective) {
    case 'fade':
      return 'animate-out fade-out-0 duration-200 fill-mode-both'
    case 'crisp':
      return `animate-out fade-out-0 ${toCrisp} duration-150 fill-mode-both`
    case 'nudge':
    default:
      return `animate-out fade-out-0 ${to} duration-200 fill-mode-both`
  }
}
